import React from 'react';
import PropTypes from 'prop-types';
import {action, computed, observable} from 'mobx';
import {inject, observer} from 'mobx-react';
import {Link} from 'react-router';
import autobind from 'autobind-decorator';

import {withConfig, withI18n} from 'spa/Components/Context';
import {CheckboxGroup} from 'spa/Components/Form';
import Popup from 'spa/Components/Popup';
import Alert from 'spa/Components/Alert';
import LoadingIndicator from 'spa/Components/LoadingIndicator';

import moment from 'moment-timezone';

import './CitySelectPopup.less';
import closeIcon from 'spa/images/icons/cancel-medium-light.svg';

@inject(({store: {tracker, cities}}) => (
  {tracker, cities}
))
@observer
@withConfig
@withI18n
class CitySelectPopup extends React.Component {
  static propTypes = {
    cities: PropTypes.shape({
      maybeLoadSearch: PropTypes.func.isRequired,
      getSearch: PropTypes.func.isRequired,
    }).isRequired,
    isSignup: PropTypes.bool,
    onClick: PropTypes.func,
    onClose: PropTypes.func,
    selectedCity: PropTypes.shape({
      id: PropTypes.string,
    }),
  }

  static defaultProps = {
    isSignup: false,
    selectedCity: {
      id: '',
    },
  }

  UNSAFE_componentWillMount() {
    this.props.cities.maybeLoadSearch({});

    const {config, i18n} = this.context;

    this.newsletterCheckbox = [
      {
        value: 'newsletter',
        label: i18n._x('Fancy receiving stuff like exclusive offers, news, competitions, events and content? Just say yes to marketing comms.', 'Signup Form'),
        name: 'newsletterFB',
      },
    ];

    this.termsCheckbox = [
      {
        value: 'acceptTermsConditions',
        label: i18n.__react('Yes, I agree to the %(termsLink)s and the %(privacyLink)s of Urban.', {
          termsLink: <Link className="link" to={`/${i18n.lc}/legal`} target="_blank" rel="noopener noreferrer">{i18n._x('Terms and Conditions', 'Signup form link text')}</Link>,
          privacyLink: <Link className="link" to={`/${i18n.lc}/privacy-policy`} target="_blank" rel="noopener noreferrer">{i18n._x('Privacy Policy', 'Signup form link text')}</Link>,
        }, 'Signup form Austria accept terms'),
        name: 'acceptTermsConditionsFB',
      },
    ];

    this.showAcceptTC = config.getValue('compliance.user-show-accept-latest-terms-and-conditions', i18n.umRegion);
    this.showAcceptMarketing = config.getValue('compliance.user-show-accept-marketing-communications', i18n.umRegion);
  }

  @observable acceptTermsError = false;
  @observable selectedCity = false;

  /*
   * Update accept terms and conditions
   */
  @autobind
  @action('onChangeTerms - CitySelectPopup')
  onChangeTerms(event) {
    const {target} = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    this.acceptedTerms = value;
  }

  /*
   * Update signup newsletter
   */
  @autobind
  @action('onChangeNewsletter - CitySelectPopup')
  onChangeNewsletter(event) {
    const {target} = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    this.acceptedNewsletter = value;
  }

  /*
   * Close popup when user clicks close button
   */
  @action('dismissPopup - CitySelectPopup')
  dismissPopup = (event) => {
    if (event) event.preventDefault();

    this.props.onClose();
  }

  @action('selectCity - CitySelectPopup')
  selectCity = (event) => {
    const {isSignup} = this.props;
    const {showAcceptMarketing, showAcceptTC} = this;

    this.props.selectedCity.id = event.currentTarget.dataset.city;

    // If this is not signup, user doesn't have to accept terms and can be logged in
    if (!isSignup || (!showAcceptTC && !showAcceptMarketing)) {
      this.props.onClick(this.props.selectedCity);

      return null;
    }

    this.selectedCity = true;
  }

  /*
   *  Submit the signup after accepting terms and conditions
   */
  @action('submitSignup - CitySelectPopup')
  submitSignup = (event) => {
    if (event) event.preventDefault();

    const {acceptedNewsletter, acceptedTerms, showAcceptTC} = this;

    if (!acceptedTerms && showAcceptTC) {
      this.acceptTermsError = true;

      return null;
    }

    const acceptedMarketingOptInAt = acceptedNewsletter ? moment().toISOString() : null;

    this.props.onClick(this.props.selectedCity, acceptedMarketingOptInAt);
  }

  @computed get citiesSearch() {
    setTimeout(() => this.props.cities.maybeLoadSearch({}), 0);

    return this.props.cities.getSearch({});
  }

  @computed get activeCities() {
    const {instances} = this.citiesSearch;

    if (instances) {
      return instances.filter(city => city.visible);
    }

    return null;
  }

  render() {
    const {
      activeCities, newsletterCheckbox, selectedCity, submitSignup, termsCheckbox,
    } = this;
    const {i18n} = this.context;
    const {isSignup} = this.props;

    const search = this.citiesSearch;
    const {isLoading, error} = search;

    return (
      <Popup>
        <div className="city-select-popup">
          <h1 className="city-select-popup__title">{i18n.__('Select Your City')}</h1>
          <div className="city-select-popup__close-button" onClick={this.dismissPopup}>
            <img width="20" height="20" src={closeIcon} alt="" />
          </div>
          <div className="city-select-popup_body">
            {error &&
            <Alert type="error" title={i18n.__error(error)} />
            }
            {isLoading &&
            <LoadingIndicator style={{color: '#333'}} />
            }
            {!selectedCity && activeCities &&
            <ul className="city-select-popup_list">
              {activeCities.map(city => (
                <li
                  className="city-select-popup_list-element"
                  onClick={this.selectCity}
                  data-city={city.id}
                  key={city.id}>
                  {city.name}
                </li>
              ))}
            </ul>
            }
            {(this.showAcceptTC || this.showAcceptMarketing) && selectedCity && isSignup && (
              <div className="city-select-popup__checkbox-container form-group">
                {this.showAcceptTC && (
                  <CheckboxGroup
                    onChange={this.onChangeTerms}
                    items={termsCheckbox}
                    id="acceptTermsConditionsFB"
                  />
                )}
                {this.showAcceptMarketing && (
                  <CheckboxGroup
                    onChange={this.onChangeNewsletter}
                    items={newsletterCheckbox}
                    id="newsletterFB"
                  />
                )}
                {this.acceptTermsError && (
                  <p className="signup-page__error form-field-error" data-qa="accept-terms-error">{i18n._x('You must accept the Terms & Conditions', 'Signup form')}</p>
                )}
                <button
                  onClick={submitSignup}
                  className="button button--default button--primary city-select-popup__submit-button">
                  {i18n._x('submit', 'Facebook signup submit button')}
                </button>
              </div>
            )}
          </div>
        </div>
      </Popup>
    );
  }
}

export default CitySelectPopup;



// WEBPACK FOOTER //
// ./spa/Components/CitySelectPopup.jsx